<template>
	<Modal @close="close()">
		<template slot="title">{{ sheet.title }}</template>
		<template slot="body">
			<div class="sheet_copy_popup">
				<div class="sheet_copy_toolbar">
					<a href="javascript:;" class="search_bt mgr_10" @click="copyAll()">Copy</a>
					<a href="javascript:;" class="gray_bt mgr_15" @click="close()">Close</a>
					<span class="sheet_copy_hint">{{ hint }}</span>
				</div>
				<div class="sheet_copy_table" @copy="onCopy($event)">
					<table ref="table" class="list_tbl">
						<thead>
							<tr>
								<th v-for="(name, index) in grid.header" :key="index">{{ name }}</th>
							</tr>
						</thead>
						<tbody>
							<tr v-for="(row, row_index) in grid.rows" :key="row_index" :class="row_index < grid.total_rows ? 'sheet_copy_total' : ''">
								<td v-for="(cell, cell_index) in row" :key="cell_index" :class="cell.kind === 'number' ? 'al_right' : 'al_left'">{{ cell.value }}</td>
							</tr>
						</tbody>
					</table>
				</div>
			</div>
		</template>
	</Modal>
</template>

<script>
import Modal from '@/components/common/Modal';
import { buildSheetGrid, copySheetToClipboard, toHtml, toTsv } from '@/utils/sheetCopy';

/**
 * #3312 Sheet 복사 팝업. sheet = { title, headers, rows, options } 를 받아 헤더 행 + 데이터 행을 표로 보여주고
 * Copy 로 전체(헤더 포함)를 클립보드에 넣는다. 표를 드래그해 일부만 Ctrl+C 로 복사할 때도 copy 이벤트에서
 * 선택 블록을 전체 Copy 와 같은 HTML/TSV(문자열 서식·빈칸 유지)로 바꿔 넣는다.
 * options.total_row 가 있으면(3차) 헤더 바로 아래 합계 행을 굵게 표시하고 안내·복사 결과의 행 수에서는 뺀다.
 */
export default {
	components: {
		Modal,
	},
	props: {
		sheet: { type: Object, required: true },
	},
	data: function() {
		return {
			copying: false,
		};
	},
	computed: {
		grid() {
			return buildSheetGrid(this.sheet.headers, this.sheet.rows, this.sheet.options);
		},
		data_row_count() {
			return this.grid.rows.length - (this.grid.total_rows || 0);
		},
		total_row_label() {
			return this.grid.total_rows ? ' + total row' : '';
		},
		hint() {
			return this.data_row_count + ' row(s)' + this.total_row_label + ' + header row. Click Copy, then paste into Excel. Drag cells and press Ctrl+C to copy a part.';
		},
	},
	methods: {
		async copyAll() {
			if (this.copying) return;
			this.copying = true;
			try {
				const result = await copySheetToClipboard(this.grid);
				const note = result.method === 'fallback-text' ? ' (text only)' : '';
				const copied = result.rows - (this.grid.total_rows || 0);
				this.$toast.success('Copied ' + copied + ' row(s)' + this.total_row_label + ' with header' + note + '.');
			} catch (error) {
				this.$toast.error('Copy failed. Drag the cells you need and press Ctrl+C.');
			} finally {
				this.copying = false;
			}
		},
		close() {
			this.$emit('close');
		},
		/**
		 * 드래그 선택 후 Ctrl+C·우클릭 복사. 브라우저 기본 직렬화는 셀의 문자열 서식을 싣지 않으므로
		 * 선택 셀을 감싸는 직사각형 블록을 격자에서 잘라 toHtml/toTsv 로 넣는다. 표 안에 선택이 없으면 기본 동작을 둔다.
		 */
		onCopy(event, selection = typeof window.getSelection === 'function' ? window.getSelection() : null) {
			const block = this.selectedBlock(selection);
			if (!block || !event.clipboardData) return;
			event.clipboardData.setData('text/html', toHtml(block));
			event.clipboardData.setData('text/plain', toTsv(block));
			event.preventDefault();
		},
		selectedBlock(selection) {
			const table = this.$refs.table;
			if (!table || !selection || !selection.rangeCount || selection.isCollapsed) return null;
			let min_col = Infinity;
			let max_col = -1;
			const mark = (cell, col) => {
				if (!selection.containsNode(cell, true)) return false;
				min_col = Math.min(min_col, col);
				max_col = Math.max(max_col, col);
				return true;
			};
			let header_selected = false;
			[...table.querySelectorAll('thead th')].forEach((th, col) => {
				if (mark(th, col)) header_selected = true;
			});
			const row_indexes = [];
			[...table.querySelectorAll('tbody tr')].forEach((tr, row_index) => {
				let hit = false;
				[...tr.children].forEach((td, col) => {
					if (mark(td, col)) hit = true;
				});
				if (hit) row_indexes.push(row_index);
			});
			if (max_col < 0) return null;
			const slice = cells => cells.slice(min_col, max_col + 1);
			return {
				header: header_selected ? slice(this.grid.header) : [],
				rows: row_indexes.map(row_index => slice(this.grid.rows[row_index])),
			};
		},
	},
};
</script>

<style scoped>
.sheet_copy_popup {
	width: 90vw;
	max-width: 1200px;
	height: 70vh;
	display: flex;
	flex-direction: column;
}
.sheet_copy_toolbar {
	flex: 0 0 auto;
	padding: 8px 0;
}
.sheet_copy_hint {
	color: #666;
	font-size: 12px;
}
.sheet_copy_table {
	flex: 1 1 auto;
	min-height: 0;
	overflow: auto;
	user-select: text;
}
.sheet_copy_table table {
	width: auto;
	min-width: 100%;
	white-space: nowrap;
}
.sheet_copy_table th,
.sheet_copy_table td {
	padding: 2px 6px;
}
.sheet_copy_table tr.sheet_copy_total td {
	font-weight: bold;
	background: #f4f6f8;
}
</style>
